import React, { FocusEventHandler, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import { SelectValue } from 'antd/es/select';

import './index.less';
import IbSelect, { IIbSelectProps } from '../IbSelect';
import IbSpin from '../IbSpin';

interface IIbDebounceSelectProps extends IIbSelectProps {
  fetchOptions: (search: string) => Promise<SelectValue[]>;
  debounceTimeout?: number;
  value?: SelectValue;
  showSearch?: boolean;
  disabled?: boolean;
  onBlur?: FocusEventHandler<HTMLElement>;
  onChange?: (value: SelectValue) => void;
}

const DEFAULT_DEBOUNCE_TIMEOUT = 800;

const IbDebounceSelect: React.FC<IIbDebounceSelectProps> = ({
  fetchOptions,
  debounceTimeout = DEFAULT_DEBOUNCE_TIMEOUT,
  value,
  showSearch,
  disabled,
  status,
  dropdownClassName,
  onBlur,
  onChange,
}) => {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const fetchRef = useRef(0);

  // NOTE: when using IbDebounceSelect, make sure the `fetchOptions` doesn't change too often.
  // Use useCallback on a `fetchOptions` declaration in parent component if necessary.
  useEffect(() => {
    setOptions([]);
  }, [fetchOptions]);

  const debounceFetcher = useMemo(() => {
    const loadOptions = (value: string) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setOptions([]);
      setLoading(true);

      fetchOptions(value).then((newOptions) => {
        if (fetchId !== fetchRef.current) {
          return;
        }

        setOptions(newOptions.map((o) => o as never));
        setLoading(false);
      });
    };

    return debounce(loadOptions, debounceTimeout);
  }, [fetchOptions, debounceTimeout]);

  useEffect(() => {
    setOptions([]);
    debounceFetcher('');
    return () => {
      debounceFetcher.cancel?.();
    };
  }, [debounceFetcher]);

  const onFocus: FocusEventHandler<HTMLElement> = () => {
    if (!options.length) {
      debounceFetcher('');
    }
  };

  return (
    <IbSelect
      labelInValue
      disabled={disabled ?? false}
      dropdownClassName={dropdownClassName}
      loading={loading}
      notFoundContent={loading ? <IbSpin size="small" /> : null}
      options={options}
      showSearch={showSearch ?? false}
      status={status}
      value={value}
      onBlur={onBlur}
      onChange={onChange}
      onFocus={onFocus}
      onSearch={debounceFetcher}
    />
  );
};

export default IbDebounceSelect;
